// ============================================================================
// GTO REFERENCE RANGES
// ----------------------------------------------------------------------------
// Simplified, widely published solver-based preflop ranges for no-limit
// hold'em cash games (100 big blinds, 2.5x opens) and short-stack push/fold.
// They are a reference, not a solver: real GTO mixes frequencies; these round
// each hand to one action so a player can read the chart at a glance.
//
// Range notation: "22+, A2s+, K9s+, T9s, AJo+, KQo". A hand code is rank-rank
// plus s (suited) or o (offsuit); pairs are two ranks. "+" means that hand
// and everything better of the same kind (A9s+ = A9s, ATs, AJs, AQs, AKs;
// 77+ = 77, 88 ... AA; K9o+ = K9o, KTo, KJo, KQo).
// ============================================================================

export const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'] as const;
const RANK_INDEX: Record<string, number> = Object.fromEntries(RANKS.map((r, i) => [r, i]));

export type Action = 'raise' | 'call' | 'allin' | 'fold';

/** Expand one range string into a Set of hand codes ("AKs", "T9o", "77"). */
export function expandRange(range: string): Set<string> {
  const out = new Set<string>();
  for (const raw of range.split(',')) {
    const tok = raw.trim();
    if (!tok) continue;
    const plus = tok.endsWith('+');
    const body = plus ? tok.slice(0, -1) : tok;
    // Pairs
    if (body.length === 2 && body[0] === body[1]) {
      const start = RANK_INDEX[body[0]];
      if (start === undefined) continue;
      if (plus) { for (let i = start; i >= 0; i--) out.add(RANKS[i] + RANKS[i]); }
      else out.add(body);
      continue;
    }
    // Dashes like "A5s-A2s"
    if (body.includes('-')) {
      const [hi, lo] = body.split('-').map((x) => x.trim());
      const suit = hi[2];
      const high = RANK_INDEX[hi[0]];
      const from = RANK_INDEX[hi[1]];
      const to = RANK_INDEX[lo[1]];
      if (high === undefined || from === undefined || to === undefined) continue;
      for (let i = Math.min(from, to); i <= Math.max(from, to); i++) out.add(RANKS[high] + RANKS[i] + suit);
      continue;
    }
    if (body.length !== 3) continue;
    const high = RANK_INDEX[body[0]];
    const kicker = RANK_INDEX[body[1]];
    const suit = body[2];
    if (high === undefined || kicker === undefined || (suit !== 's' && suit !== 'o')) continue;
    if (plus) {
      // Kicker climbs toward the high card (A9s+ = A9s..AKs)
      for (let i = kicker; i > high; i--) out.add(RANKS[high] + RANKS[i] + suit);
    } else {
      out.add(body);
    }
  }
  return out;
}

/** Hand code for grid cell (row, col): diagonal pairs, upper-right suited, lower-left offsuit. */
export function handAt(row: number, col: number): string {
  if (row === col) return RANKS[row] + RANKS[row];
  if (col > row) return RANKS[row] + RANKS[col] + 's';
  return RANKS[col] + RANKS[row] + 'o';
}

/**
 * Hand code ("AKs", "T9o", "77") for two dealt cards. Accepts the game's
 * card shape (rank '10' or 'T', suit as a word or a letter). Returns null
 * when fewer than two cards are known.
 */
export function handCodeFromCards(cards: Array<{ rank: string; suit: string }> | undefined | null): string | null {
  if (!cards || cards.length < 2 || !cards[0] || !cards[1]) return null;
  const norm = (r: string) => (r === '10' ? 'T' : r.toUpperCase());
  const a = norm(String(cards[0].rank));
  const b = norm(String(cards[1].rank));
  if (RANK_INDEX[a] === undefined || RANK_INDEX[b] === undefined) return null;
  if (a === b) return a + a;
  const [hi, lo] = RANK_INDEX[a] < RANK_INDEX[b] ? [a, b] : [b, a];
  const suited = String(cards[0].suit).toLowerCase()[0] === String(cards[1].suit).toLowerCase()[0];
  return hi + lo + (suited ? 's' : 'o');
}

/** Number of combos a hand code represents (pairs 6, suited 4, offsuit 12). */
export function combosOf(code: string): number {
  if (code.length === 2) return 6;
  return code.endsWith('s') ? 4 : 12;
}

export interface RangeChart {
  id: string;
  title: string;
  subtitle: string;
  /** Actions painted in order; later entries do not override earlier ones. */
  layers: Array<{ action: Action; range: string; label: string }>;
  note?: string;
}

// ── Open-raise (raise first in), 100bb cash ──────────────────────────────────
const OPEN_6MAX: Record<string, string> = {
  UTG: '22+, A2s+, K9s+, Q9s+, J9s+, T9s, 98s, 87s, 76s, AJo+, KQo',
  HJ: '22+, A2s+, K7s+, Q8s+, J8s+, T8s+, 97s+, 86s+, 75s+, 65s, ATo+, KJo+, QJo',
  CO: '22+, A2s+, K5s+, Q7s+, J7s+, T7s+, 96s+, 86s+, 75s+, 64s+, 54s, A9o+, KTo+, QTo+, JTo',
  BTN: '22+, A2s+, K2s+, Q4s+, J5s+, T6s+, 96s+, 85s+, 75s+, 64s+, 53s+, 43s, A2o+, K8o+, Q9o+, J9o+, T9o',
  SB: '22+, A2s+, K2s+, Q5s+, J6s+, T6s+, 96s+, 85s+, 75s+, 64s+, 54s, A2o+, K9o+, Q9o+, J9o+, T9o',
};
const OPEN_9MAX: Record<string, string> = {
  UTG: '66+, ATs+, KQs, AQo+',
  'UTG+1': '55+, A9s+, KTs+, QJs, JTs, AJo+, KQo',
  MP: '22+, A7s+, K9s+, Q9s+, J9s+, T9s, 98s, ATo+, KJo+',
  LJ: OPEN_6MAX.UTG,
  HJ: OPEN_6MAX.HJ,
  CO: OPEN_6MAX.CO,
  BTN: OPEN_6MAX.BTN,
  SB: OPEN_6MAX.SB,
};

// ── Facing an open raise (you are next to act, 100bb) ────────────────────────
const VS_OPEN: Record<string, { threebet: string; call: string; note: string }> = {
  'In position vs early raise': {
    threebet: 'QQ+, AKs, AKo, A5s, A4s',
    call: '22-JJ, ATs+, KJs+, QJs, JTs, T9s, 98s, 87s, AQo, KQo',
    note: 'Against a raise from an early seat, flat the medium pairs and suited broadways, 3-bet only the top and a couple of suited aces as bluffs.',
  },
  'In position vs late raise': {
    threebet: 'TT+, AJs+, KQs, AQo+, A5s-A2s, K9s, 76s, 65s',
    call: '22-99, A9s-ATs, KTs-KJs, QTs+, J9s+, T8s+, 97s+, 86s+, 75s+, AJo, KQo, KJo, QJo',
    note: 'Late raisers open wide, so 3-bet more for value and add suited bluffs; still flat the hands that play well after the flop.',
  },
  'Big blind vs button raise': {
    threebet: 'TT+, AJs+, AQo+, A5s-A2s, K9s, Q9s, J9s, 86s, 75s, 65s, 54s',
    call: '22-99, A2s-ATs, K2s+, Q4s+, J6s+, T6s+, 96s+, 85s+, 74s+, 64s+, 53s+, 43s, A2o+, K8o+, Q9o+, J9o+, T9o',
    note: 'You already have money in the pot and close the action, so defend very wide. Everything not shown is a fold.',
  },
};

// ── Push or fold (short stacks, no antes, unopened pot) ───────────────────────
const PUSH_FOLD: Record<string, Record<string, string>> = {
  '10 BB': {
    CO: '22+, A2s+, A5o+, K5s+, K9o+, Q8s+, QTo+, J8s+, JTo, T8s+, 98s',
    BTN: '22+, A2s+, A2o+, K2s+, K7o+, Q6s+, Q9o+, J7s+, J9o+, T7s+, T9o, 97s+, 86s+, 76s',
    SB: '22+, A2s+, A2o+, K2s+, K5o+, Q5s+, Q8o+, J7s+, J9o+, T7s+, T9o, 97s+, 87s, 76s',
  },
  '15 BB': {
    CO: '33+, A7s+, ATo+, KTs+, KQo, QTs+, JTs',
    BTN: '22+, A2s+, A7o+, K7s+, KTo+, Q9s+, QTo+, J9s+, JTo, T9s, 98s',
    SB: '22+, A2s+, A4o+, K5s+, K9o+, Q8s+, QTo+, J8s+, JTo, T8s+, 98s, 87s',
  },
  '20 BB': {
    CO: '55+, A9s+, AJo+, KJs+, KQo, QJs',
    BTN: '22+, A4s+, A9o+, K8s+, KJo+, Q9s+, QJo, J9s+, T9s',
    SB: '22+, A2s+, A8o+, K7s+, KTo+, Q9s+, QTo+, J9s+, JTo, T9s, 98s',
  },
};

export const TABLE_SIZES = ['6-max', '9-max'] as const;
export type TableSize = typeof TABLE_SIZES[number];
export const OPEN_POSITIONS: Record<TableSize, string[]> = {
  '6-max': ['UTG', 'HJ', 'CO', 'BTN', 'SB'],
  '9-max': ['UTG', 'UTG+1', 'MP', 'LJ', 'HJ', 'CO', 'BTN', 'SB'],
};
export const VS_OPEN_SPOTS = Object.keys(VS_OPEN);
export const PUSH_STACKS = Object.keys(PUSH_FOLD);
export const PUSH_POSITIONS = ['CO', 'BTN', 'SB'];

export const POSITION_NAMES: Record<string, string> = {
  UTG: 'Under the gun (first to act)',
  'UTG+1': 'Second seat',
  MP: 'Middle position',
  LJ: 'Lojack',
  HJ: 'Hijack',
  CO: 'Cutoff',
  BTN: 'Button (dealer)',
  SB: 'Small blind',
};

export function openChart(size: TableSize, position: string): RangeChart {
  const src = size === '6-max' ? OPEN_6MAX : OPEN_9MAX;
  return {
    id: `open-${size}-${position}`,
    title: `Open raise from the ${position}`,
    subtitle: `${size}, 100 big blinds, nobody has entered the pot`,
    layers: [{ action: 'raise', range: src[position] || '', label: 'Raise' }],
    note: position === 'SB'
      ? 'From the small blind many players mix in limps; this chart shows the raise-first version. Everything not shown is a fold.'
      : 'Everything not shown is a fold. Early seats are tight because more players act after you.',
  };
}

export function vsOpenChart(spot: string): RangeChart {
  const s = VS_OPEN[spot];
  return {
    id: `vs-${spot}`,
    title: spot,
    subtitle: '100 big blinds, one raise in front of you',
    layers: [
      { action: 'raise', range: s.threebet, label: '3-bet (re-raise)' },
      { action: 'call', range: s.call, label: 'Call' },
    ],
    note: s.note,
  };
}

export function pushFoldChart(stack: string, position: string): RangeChart {
  return {
    id: `push-${stack}-${position}`,
    title: `Push or fold: ${position} with ${stack}`,
    subtitle: 'Unopened pot, no antes. Move all-in with these, fold the rest.',
    layers: [{ action: 'allin', range: PUSH_FOLD[stack][position], label: 'All-in' }],
    note: 'Short-stack tournament play. With antes in play you can shove a little wider than shown.',
  };
}

/** Paint the 13x13 grid for a chart: hand code -> action. */
export function paintChart(chart: RangeChart): { grid: Action[][]; percent: number; counts: Record<Action, number> } {
  const painted = new Map<string, Action>();
  for (const layer of chart.layers) {
    for (const code of expandRange(layer.range)) if (!painted.has(code)) painted.set(code, layer.action);
  }
  const grid: Action[][] = [];
  const counts: Record<Action, number> = { raise: 0, call: 0, allin: 0, fold: 0 };
  let combos = 0;
  for (let r = 0; r < 13; r++) {
    const row: Action[] = [];
    for (let c = 0; c < 13; c++) {
      const code = handAt(r, c);
      const action = painted.get(code) || 'fold';
      row.push(action);
      counts[action] += 1;
      if (action !== 'fold') combos += combosOf(code);
    }
    grid.push(row);
  }
  return { grid, percent: Math.round((combos / 1326) * 100), counts };
}
